import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { io, Socket } from "socket.io-client";
import { getOrCreateTabId } from "./useTabId";
import { CloudEvent } from "@/types/cloud-events";

interface UseWebSocketOptions {
  onMessage?: (message: CloudEvent) => void;
  onError?: (error: Event) => void;
  onOpen?: (event: Event) => void;
  onClose?: (event: CloseEvent) => void;
  reconnectInterval?: number;
  reconnectAttempts?: number;
  heartbeatInterval?: number;
  enabled?: boolean;
  platform?: string;
  tabId?: string; // Optional tab ID, will be auto-generated if not provided
}

// Build Socket.IO URL from region and JWT
const buildSocketIOUrl = (
  region: string = "us",
  jwt: string,
  platform: string = "web",
  tabId?: string,
): { url: string; options: any } => {
  // Use dedicated WebSocket URL if available, otherwise fall back to API URL
  const wsBaseUrl =
    process.env.NEXT_PUBLIC_WEBSOCKET_URL || process.env.NEXT_PUBLIC_API_URL;
  const baseUrl = wsBaseUrl?.replace("{region}", region) || "";

  // Remove trailing slash
  const socketUrl = baseUrl.replace(/\/$/, "");

  const options = {
    path: "/events",
    auth: {
      token: jwt,
      platform: platform,
      ...(tabId && { tabId }), // Include tabId if provided
    },
    transports: ["websocket", "polling"],
    autoConnect: true,
    // Reconnection settings for resilience during navigation
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    timeout: 10000,
  };

  return { url: socketUrl, options };
};

export const useWebSocketConnection = (options: UseWebSocketOptions = {}) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState<
    "connecting" | "open" | "closing" | "closed" | "uninstantiated"
  >("uninstantiated");
  const [lastMessage, setLastMessage] = useState<any>(null);
  // Increments only on a genuine reconnect (disconnected -> connected), never
  // on the very first connect. Consumers key reconnect-driven reconciliation
  // (e.g. refetch-after-outage) off this signal.
  const [reconnectNonce, setReconnectNonce] = useState(0);
  // Tracks whether the socket has been disconnected since its last successful
  // connect, so the "connect" handler can distinguish a first connect from a
  // reconnect.
  const wasDisconnectedRef = useRef(false);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const socketRef = useRef<Socket | null>(null); // Ref to track current socket for cleanup

  const {
    onMessage,
    onError,
    onOpen,
    onClose,
    heartbeatInterval = 30000,
    enabled = true,
    platform = "web",
    tabId: providedTabId,
  } = options;

  // Generate or use provided tab ID - memoize to prevent recalculation on every render
  const tabId = useMemo(() => {
    return providedTabId || getOrCreateTabId();
  }, [providedTabId]);

  // Fetch JWT from session endpoint. The Stytch JWT lives for JWT_MAX_AGE (5
  // minutes), so treat it as fresh for 4: without a staleTime the query is
  // always stale, and every tab focus / `online` event re-hits the endpoint,
  // which re-authenticates against Stytch and rotates the session cookies.
  const { data: sessionData } = useQuery({
    queryKey: ["websocket-session"],
    queryFn: async () => {
      try {
        const response = await fetch("/api/auth/session");
        return await response.json();
      } catch (error) {
        console.error("Failed to fetch session:", error);
        throw error;
      }
    },
    enabled,
    staleTime: 4 * 60 * 1000,
    refetchInterval: enabled ? 4 * 60 * 1000 : false, // Refetch every 4 minutes if enabled
    // A failed refresh is retried by the next interval tick; the default
    // retry: 3 turns one 5xx into four calls from every client at once.
    retry: false,
  });

  // Store callbacks in refs to avoid triggering re-renders
  const callbacksRef = useRef({
    onMessage,
    onOpen,
    onClose,
    onError,
  });

  // Update callbacks ref when they change
  useEffect(() => {
    callbacksRef.current = {
      onMessage,
      onOpen,
      onClose,
      onError,
    };
  }, [onMessage, onOpen, onClose, onError]);

  // Heartbeat mechanism - use socketRef instead of socket state
  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  }, []);

  const startHeartbeat = useCallback(() => {
    stopHeartbeat();
    heartbeatIntervalRef.current = setInterval(() => {
      const currentSocket = socketRef.current;
      if (currentSocket && currentSocket.connected) {
        currentSocket.emit("ping", { timestamp: new Date().toISOString() });
      }
    }, heartbeatInterval);
  }, [heartbeatInterval, stopHeartbeat]);

  // Create and manage Socket.IO connection when session data changes
  useEffect(() => {
    // If disabled, disconnect any existing socket and return
    if (!enabled) {
      const currentSocket = socketRef.current;
      if (currentSocket) {
        stopHeartbeat();
        currentSocket.disconnect();
        socketRef.current = null;
        setSocket(null);
        setIsConnected(false);
        setConnectionState("uninstantiated");
        setConnectionError(null);
      }
      return;
    }

    if (sessionData?.member && sessionData?.session_jwt) {
      const region = sessionData.member?.trusted_metadata?.region || "us";
      const jwt = sessionData.session_jwt;

      // Don't create a new socket if one already exists and is connected
      if (socketRef.current && socketRef.current.connected) {
        return;
      }

      // Clean up existing socket if it exists but isn't connected
      if (socketRef.current) {
        stopHeartbeat();
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
      }

      if (jwt) {
        const { url, options: socketOptions } = buildSocketIOUrl(
          region,
          jwt,
          platform,
          tabId,
        );

        const newSocket = io(url, socketOptions);

        // Connection event handlers - use callbacks from ref
        newSocket.on("connect", () => {
          setIsConnected(true);
          setConnectionState("open");
          setConnectionError(null);
          startHeartbeat();
          // Only bump the reconnect nonce when this is a genuine reconnect
          // (we were previously disconnected), not on the first connect.
          if (wasDisconnectedRef.current) {
            wasDisconnectedRef.current = false;
            setReconnectNonce((nonce) => nonce + 1);
          }
          callbacksRef.current.onOpen?.(new Event("open"));
        });

        newSocket.on("disconnect", (reason) => {
          setIsConnected(false);
          setConnectionState("closed");
          wasDisconnectedRef.current = true;
          stopHeartbeat();

          if (reason === "transport close" || reason === "transport error") {
            setConnectionError("Backend API server unavailable");
          } else {
            setConnectionError(`Connection closed: ${reason}`);
          }

          const closeEvent = new CloseEvent("close", {
            code: reason === "transport close" ? 1006 : 1000,
            reason: reason,
          });
          callbacksRef.current.onClose?.(closeEvent);
        });

        newSocket.on("connect_error", () => {
          setConnectionError("Failed to connect to WebSocket server");
          setConnectionState("closed");
          // Don't call onError for transient connection errors - Socket.IO handles retry
        });

        newSocket.on("reconnect_failed", () => {
          console.warn("WebSocket reconnection failed after all attempts");
          callbacksRef.current.onError?.(new Event("error"));
        });

        // Handle protocol-level Socket.IO events (not CloudEvents)
        // These are for internal connection management, not domain events
        newSocket.on("connected", (data) => {
          setLastMessage({
            data: JSON.stringify({ type: "connected", payload: data }),
          });
          // Protocol event - don't call onMessage (expects CloudEvents)
        });

        newSocket.on("pong", () => {
          // Heartbeat response - don't trigger onMessage
        });

        newSocket.on("subscribed", (data) => {
          setLastMessage({
            data: JSON.stringify({ type: "subscribed", payload: data }),
          });
          // Protocol event - don't call onMessage (expects CloudEvents)
        });

        newSocket.on("unsubscribed", (data) => {
          setLastMessage({
            data: JSON.stringify({ type: "unsubscribed", payload: data }),
          });
          // Protocol event - don't call onMessage (expects CloudEvents)
        });

        // Handle CloudEvents from the backend
        // The backend emits events where the event name is the CloudEvent type
        // and the data is the full CloudEvent object
        newSocket.onAny((event, data) => {
          if (
            !["connect", "disconnect", "connect_error", "pong"].includes(event)
          ) {
            // CloudEvents come with the full envelope in data
            // The event name matches data.type (e.g., "com.focal.task.created")
            if (data && typeof data === "object" && data.type && data.data) {
              // This is a CloudEvent
              setLastMessage({ data: JSON.stringify(data) });
              callbacksRef.current.onMessage?.(data as CloudEvent);
            }
          }
        });

        setSocket(newSocket);
        socketRef.current = newSocket; // Update ref
        setConnectionState("connecting");
      }
    }

    // Cleanup function - use ref to access current socket without needing it in dependencies
    return () => {
      const currentSocket = socketRef.current;
      if (currentSocket) {
        stopHeartbeat();
        currentSocket.disconnect();
        socketRef.current = null;
        setSocket(null);
        setIsConnected(false);
        setConnectionState("uninstantiated");
      }
    };
  }, [
    sessionData?.session_jwt,
    enabled,
    stopHeartbeat,
    startHeartbeat,
    platform,
    tabId,
  ]); // Removed callbacks from dependencies - using refs instead

  // Send message (for protocol events like subscribe/unsubscribe)
  const sendMessage = useCallback(
    (message: { type: string; payload?: Record<string, unknown> }) => {
      if (socket && socket.connected) {
        const { type, payload } = message;
        const eventName = type || "message";
        const eventData = {
          ...payload,
          timestamp: new Date().toISOString(),
        };

        socket.emit(eventName, eventData);
      }
    },
    [socket],
  );

  // Subscribe to a specific channel (e.g., conversation updates)
  const subscribe = useCallback((channel: string, entityId?: string) => {
    const currentSocket = socketRef.current;
    if (currentSocket && currentSocket.connected) {
      const payload = entityId ? { channel, entityId } : { channel };
      currentSocket.emit("subscribe", payload);
    }
  }, []);

  // Unsubscribe from a specific channel
  const unsubscribe = useCallback((channel: string, entityId?: string) => {
    const currentSocket = socketRef.current;
    if (currentSocket && currentSocket.connected) {
      const payload = entityId ? { channel, entityId } : { channel };
      currentSocket.emit("unsubscribe", payload);
    }
  }, []);

  // Get Socket.IO URL for debugging
  const getSocketUrl = useCallback(() => {
    if (sessionData?.member && sessionData?.session_jwt) {
      const region = sessionData.member?.trusted_metadata?.region || "us";
      const jwt = sessionData.session_jwt;
      if (jwt) {
        const { url, options: socketOptions } = buildSocketIOUrl(
          region,
          jwt,
          platform,
          tabId,
        );
        return `${url}${socketOptions.path}`;
      }
    }
    return null;
  }, [sessionData, platform, tabId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopHeartbeat();
      const currentSocket = socketRef.current;
      if (currentSocket) {
        currentSocket.disconnect();
        socketRef.current = null;
      }
    };
  }, [stopHeartbeat]);

  // Memoize socketUrl to prevent recalculation on every render
  const socketUrl = useMemo(() => getSocketUrl(), [getSocketUrl]);

  return {
    sendMessage,
    subscribe,
    unsubscribe,
    lastMessage,
    isConnected,
    connectionState,
    connectionError,
    socketUrl,
    reconnectNonce,
  };
};
